import { useState, useEffect, useRef, useCallback } from 'react'

import { ParsedResponse as YelpWaitlistData } from '@/types/yelp'

/**
 * Custom hook to handle Yelp waitlist data fetching based on intersection observation.
 * This delays fetching until the location item scrolls into the user's view.
 *
 * @param businessId - The Yelp business ID.
 * @param isReservationOnly - If true, waitlist is not applicable.
 * @param isRestaurantOpen - If false, resturant is closed, waitlist is not applicable.
 */
const useYelpWaitlist = (
  businessId: string | null,
  isReservationOnly: boolean,
  isRestaurantOpen: boolean | null
) => {
  const locationRef = useRef<HTMLElement | null>(null)
  const [hasLoadedWaitlist, setHasLoadedWaitlist] = useState(false)
  const [isLoadingWaitlist, setIsLoadingWaitlist] = useState(false)
  const [waitlistData, setWaitlistData] = useState<YelpWaitlistData | null>(null)

  const loadWaitlist = useCallback(async () => {
    setIsLoadingWaitlist(true)

    try {
      if (!businessId) {
        throw new Error('No business ID found')
      }

      if (isReservationOnly) {
        throw new Error('Location is reservation only')
      }

      if (!isRestaurantOpen) {
        throw new Error('Restaurant is closed')
      }

      const response = await fetch(`/api/yelp/${businessId}?location=${businessId}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setWaitlistData(data)
    } catch (error) {
      console.error('Error fetching waitlist data:', error)
      setWaitlistData(null)
    } finally {
      setIsLoadingWaitlist(false)
      setHasLoadedWaitlist(true)
    }
  }, [businessId, isRestaurantOpen, isReservationOnly])

  useEffect(() => {
    if (!locationRef.current) return

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && !hasLoadedWaitlist) {
        loadWaitlist()
      }
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.1, // Triggers when at least 10% of the element is visible
    })

    observer.observe(locationRef.current)

    return () => {
      observer.disconnect()
    }
  }, [hasLoadedWaitlist, loadWaitlist])

  return {
    locationRef,
    waitlistData,
    isLoadingWaitlist,
  }
}

export default useYelpWaitlist
